import { Helmet } from "react-helmet-async";

export default function Handelsbetingelser() {
  return (
    <div className="bg-white min-h-screen pt-32 pb-24">
      <Helmet>
        <title>Handelsbetingelser & Bookingvilkår – Ta' JA-Hatten På!</title>
        <meta 
          name="description" 
          content="Læs handelsbetingelser, betalingsbetingelser og bookingvilkår for foredrag, oplæg og ydelser hos Ta' JA-Hatten På! v/ Candra Gaarde." 
        />
      </Helmet>

      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        {/* Header */}
        <div className="border-b border-slate-100 pb-12 mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-red-600 font-bold mb-4">Aftalevilkår</p>
          <h1 className="text-4xl md:text-5xl font-normal text-slate-900 tracking-tight leading-tight mb-6">
            Handels- & Bookingbetingelser
          </h1>
          <p className="text-slate-500 text-sm">
            Senest opdateret: 3. juni 2026
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-slate max-w-none space-y-12 text-slate-600 leading-relaxed font-light">
          <section className="space-y-4">
            <h2 className="text-2xl font-serif italic text-slate-900 font-normal">1. Gyldighed og anvendelse</h2>
            <p>
              Disse handels- og bookingbetingelser gælder for alle aftaler om foredrag, oplæg, workshops og andre ydelser leveret af <strong>Ta' Ja-Hatten På!</strong> (CVR: 46313291), ejet af Candra Gaarde.
            </p>
            <p>
              Vilkårene er gældende for både offentlige institutioner, private virksomheder, foreninger og privatpersoner, medmindre andet er skriftligt aftalt i en særskilt kontrakt.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-serif italic text-slate-900 font-normal">2. Priser og betaling</h2>
            <p>
              Da alle foredrag og oplæg skræddersys til den enkelte målgruppe, er priserne variable og aftales individuelt i forbindelse med tilbudsgivningen.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Moms:</strong> Alle priser opgives som udgangspunkt i danske kroner (DKK). Da foredrag og undervisning i visse tilfælde kan være fritaget for moms jf. momslovens regler, vil det fremgå eksplicit af dit tilbud og din faktura, om prisen er tillagt moms.
              </li>
              <li>
                <strong>Betalingsbetingelser:</strong> Betaling sker via bankoverførsel eller EAN-fakturering. Standard betalingsfrist er 14 dage fra fakturadato, medmindre andet er aftalt. Fakturaen fremsendes typisk umiddelbart efter foredragets afholdelse.
              </li>
              <li>
                <strong>Transportomkostninger:</strong> Transportudgifter (f.eks. kørsel efter statens takster, broafgift eller færgebilletter) tillægges prisen og aftales altid på forhånd i tilbuddet.
              </li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-serif italic text-slate-900 font-normal">3. Booking og bekræftelse</h2>
            <p>
              En aftale anses for bindende for begge parter, når der foreligger en skriftlig bekræftelse via e-mail eller underskrevet kontrakt. I bekræftelsen fastlægges dato, tidspunkt, varighed, emne, målgruppe, lokation samt den aftalte pris.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-serif italic text-slate-900 font-normal">4. Afbestilling og flytning af dato</h2>
            <p>
              Skulle det blive nødvendigt at aflyse eller flytte et booket foredrag, gælder følgende vilkår:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Afbestilling mere end 30 dage før afholdelse:</strong> Kan ske omkostningsfrit.
              </li>
              <li>
                <strong>Afbestilling mellem 14 og 30 dage før afholdelse:</strong> Faktureres med 50% af det aftalte honorar.
              </li>
              <li>
                <strong>Afbestilling mindre end 14 dage før afholdelse:</strong> Faktureres med 100% af det aftalte honorar.
              </li>
              <li>
                <strong>Flytning af dato:</strong> Vi forsøger altid at være fleksible. Hvis du ønsker at flytte foredraget til en ny dato, og dette meddeles senest 14 dage før afholdelse, kan det som udgangspunkt gøres uden ekstra beregning, forudsat at der kan findes en ledig dato i kalenderen.
              </li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-serif italic text-slate-900 font-normal">5. Force Majeure og sygdom</h2>
            <p>
              I tilfælde af akut sygdom hos foredragsholderen (Candra Gaarde) eller andre uforudsete force majeure-hændelser (f.eks. ekstreme vejrforhold, naturkatastrofer eller lignende, der umuliggør transport), forpligter vi os til hurtigst muligt at underrette kunden.
            </p>
            <p>
              I sådanne tilfælde vil vi tilbyde at flytte foredraget til en ny dato uden beregning. Hvis dette ikke er muligt eller ønskeligt for kunden, bortfalder aftalen, og eventuelt forudbetalt honorar tilbagebetales fuldt ud. Ta' Ja-Hatten På! kan dog ikke holdes økonomisk ansvarlig for kundens eventuelle tab, merudgifter eller følgeomkostninger i forbindelse med en aflysning grundet sygdom eller force majeure.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-serif italic text-slate-900 font-normal">6. Optagelse og ophavsret</h2>
            <p>
              Alt materiale, herunder slides, redskaber, koncepter (f.eks. SenseBridge) og mundtlig formidling, er beskyttet af ophavsretsloven og tilhører udelukkende Ta' Ja-Hatten På!.
            </p>
            <p>
              Lyd- og videooptagelser af foredraget er <strong>ikke tilladt</strong> uden forudgående skriftlig aftale. Det er dog meget velkomment at tage billeder til internt brug eller deling på sociale medier, så længe det sker med respekt for foredragsholderen og publikum. Tag os gerne med <code>@candragaarde</code> eller <code>#tajahattenpaa</code>.
            </p>
          </section>

          <section className="space-y-4 pb-8">
            <h2 className="text-2xl font-serif italic text-slate-900 font-normal">7. Lovvalg og værneting</h2>
            <p>
              Enhver tvist mellem kunden og Ta' Ja-Hatten På! skal søges løst i mindelighed. Hvis en mindelig løsning ikke kan opnås, skal tvisten afgøres efter dansk ret ved Retten i Esbjerg som første instans.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
